import './index.scss';

import PropTypes from 'prop-types';
import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

/**
 * Sticky proxy for another element's horizontal scrollbar.
 *
 * A native scrollbar is always drawn at the bottom edge of its own scroll
 * container, so for tall content it can sit below the viewport. This renders
 * a slim scrollable track (position it with a sticky wrapper) containing a
 * spacer as wide as the target's scrollWidth, and keeps the two scroll
 * positions in sync both ways. Assigning an unchanged scrollLeft fires no
 * scroll event, so the two listeners can't feed back into each other.
 *
 * The track hides itself while the target doesn't overflow. Width changes are
 * tracked by observing the target and its direct children, so children added
 * after mount are only picked up on the next remount.
 */
export default function StickyScrollbar({ scrollRef }) {
  const trackRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [scrollWidth, setScrollWidth] = useState(0);

  useEffect(() => {
    const scroller = scrollRef.current;
    const track = trackRef.current;
    if (!scroller || !track) return () => {};

    const syncFromScroller = () => { track.scrollLeft = scroller.scrollLeft; };
    const syncFromTrack = () => { scroller.scrollLeft = track.scrollLeft; };

    const resizeObserver = new ResizeObserver(() => {
      setScrollWidth(scroller.scrollWidth);
      setHasOverflow(scroller.scrollWidth > scroller.clientWidth);
      syncFromScroller();
    });
    resizeObserver.observe(scroller);
    Array.from(scroller.children).forEach((child) => resizeObserver.observe(child));

    scroller.addEventListener('scroll', syncFromScroller, { passive: true });
    track.addEventListener('scroll', syncFromTrack, { passive: true });

    return () => {
      resizeObserver.disconnect();
      scroller.removeEventListener('scroll', syncFromScroller);
      track.removeEventListener('scroll', syncFromTrack);
    };
  }, [scrollRef]);

  return (
    <div
      ref={trackRef}
      className="stickyScrollbar"
      hidden={!hasOverflow}
      aria-hidden="true"
    >
      <div className="spacer" style={{ inlineSize: `${scrollWidth}px` }} />
    </div>
  );
}

StickyScrollbar.propTypes = {
  // ref to the horizontally scrolling element this scrollbar controls
  // eslint-disable-next-line react/forbid-prop-types
  scrollRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
};
